import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePen, FileSignature, Megaphone, Unlock } from "lucide-react";
import { useState } from "react";

import { ApiError, api } from "../../api/client";
import type { RotationSummary, Wingo, WingoApplied } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { EmptyState } from "../../components/EmptyState";
import { Presence } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { t, tServer } from "../../i18n/i18n";
import { designateDossierReadOnly, useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { ExportMenu } from "./ExportMenu";
import { OrderPage } from "./OrderPage";
import { WingoDialog } from "./RotationDialogs";
import { WINGO_STATES, wingoNumber, wingoStatus } from "./rotationSteps";
import { SignaturePad } from "./SignaturePad";

/** « Rouvrir » du WINGO : désigné quand une modification demande un WINGO généré (pas encore diffusé). */
export const WINGO_REOPEN = "wingo-reopen";

/** Données relues après une génération ou une diffusion de WINGO : rotation, OPO annoncé, dossier, carte. */
export function refreshAfterWingo(
  queryClient: ReturnType<typeof useQueryClient>,
  exerciseId: string,
  result?: WingoApplied,
): void {
  if (result) queryClient.setQueryData(["rotation", exerciseId], result.rotation);
  for (const key of [["rotation", exerciseId], ["wingo", exerciseId], ["exercises"], ["deployment"], ["fragos", exerciseId]]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
  if (result) {
    void queryClient.invalidateQueries({ queryKey: ["order", exerciseId, result.order_number] });
    void queryClient.invalidateQueries({ queryKey: ["order-layout", exerciseId, result.order_number] });
  }
}

/** Le WINGO qui clôt l'OPO `opo` (registre A120), à la place de l'OPO dans la vue de l'ordre : pages identiques au
 *  PDF, état (à générer, généré, diffusé), « Compléter le WINGO » (situation de l'ennemi, mission et idée de
 *  manœuvre transitoires, Kill Box, FRAGO LOG, précisions des missions), signature, réouverture, exports. Sa
 *  génération et sa diffusion sont l'action de l'étape, en tête de vue. */
export function WingoView({ exerciseId, opo, summary }: { exerciseId: string; opo: number; summary: RotationSummary }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const writeBlock = useWriteBlock();
  const locked = writeBlock.blocked;
  const [dialog, setDialog] = useState<null | "complete" | "signature">(null);
  const number = wingoNumber(opo);
  const row = summary.opos[opo - 1];
  const status = wingoStatus(row);
  const wingoKey = ["wingo", exerciseId, number];
  const wingoQuery = useQuery({
    queryKey: wingoKey,
    queryFn: () => api.wingo(exerciseId, number),
    enabled: status !== null,
    refetchOnWindowFocus: true,
  });
  const wingo = wingoQuery.data;
  const layoutQuery = useQuery({
    queryKey: ["wingo-layout", exerciseId, number, wingo?.version, wingo?.has_signature, wingo?.language, wingo?.status],
    queryFn: () => api.wingoLayout(exerciseId, number),
    enabled: Boolean(wingo),
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === number ? prev : undefined),
  });
  const applied = (data: Wingo) => {
    queryClient.setQueryData(wingoKey, data);
    void queryClient.invalidateQueries({ queryKey: ["rotation", exerciseId] });
  };
  const failed = (err: unknown) =>
    notify({ text: err instanceof ApiError ? err.message : t("Opération impossible"), tone: "error" });
  const reopen = useMutation({
    mutationFn: () => api.reopenWingo(exerciseId, number),
    onSuccess: (data) => {
      applied(data);
      notify({ text: t("WINGO N°{n} rouvert : il suit de nouveau les résultats ; diffusez-le quand il est relu.", { n: number }), tone: "info" });
    },
    onError: failed,
  });
  const sign = useMutation({
    mutationFn: (png: string) => api.signWingo(exerciseId, number, png),
    onSuccess: (data) => {
      applied(data);
      setDialog(null);
    },
    onError: failed,
  });
  const unsign = useMutation({
    mutationFn: () => api.unsignWingo(exerciseId, number),
    onSuccess: (data) => {
      applied(data);
      setDialog(null);
    },
    onError: failed,
  });

  const finalized = status === "finalized";
  const captured = Boolean(row?.captured);
  const base = `/api/exercises/${exerciseId}/wingos/${number}`;
  const imageUrl = (name: string) => (name === "signature" && wingo ? `${base}/signature.png?v=${wingo.version}` : null);
  const lockedProps = locked ? { blocked: true, onBlocked: () => designateDossierReadOnly() } : {};
  const complete = () => {
    if (locked) return designateDossierReadOnly();
    if (finalized) {
      designate(attentionSelector(WINGO_REOPEN));
      notify({ text: t("WINGO diffusé : rouvrez-le d'abord pour le modifier."), tone: "info" });
      return;
    }
    setDialog("complete");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2">
        <span className="text-ui font-semibold text-ink">{t("WINGO N°{n}", { n: number })}</span>
        <Badge tone={finalized ? "ok" : status ? "accent" : "neutral"}>
          {t(WINGO_STATES[finalized ? "fait" : status ? "en_cours" : "a_faire"])}
        </Badge>
        <span className="text-meta text-ink-muted max-md:hidden">
          {t("Ordre d'avertissement : clôt l'OPO N°{n}, annonce l'OPO N°{next}", { n: opo, next: opo + 1 })}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {captured && (
            <Button size="sm" variant="ghost" onClick={complete} blocked={locked || finalized} onBlocked={complete}>
              <FilePen size={14} /> {status ? t("Compléter le WINGO") : t("Compléter avant de générer")}
            </Button>
          )}
          {status && !finalized && (
            <Button size="sm" variant="ghost" onClick={() => setDialog("signature")} {...lockedProps}>
              <FileSignature size={14} /> {wingo?.has_signature ? t("Signé") : t("Signer")}
            </Button>
          )}
          {finalized && (
            <Button size="sm" variant="secondary" data-attention={WINGO_REOPEN} onClick={() => reopen.mutate()} disabled={reopen.isPending} {...lockedProps}>
              <Unlock size={14} /> {reopen.isPending ? <Waiting>{t("Réouverture…")}</Waiting> : t("Rouvrir")}
            </Button>
          )}
          {status && (
            <ExportMenu
              formats={["pdf", "docx", "odt", "txt"]}
              href={(fmt) => `${base}/export/${fmt}`}
              title={t("Exporter le WINGO")}
              variant="secondary"
              blocked={Boolean(wingo?.translating?.length)}
              onBlocked={() => notify({ text: t("Traduction en cours : l'export sera possible dans un instant."), tone: "info" })}
            />
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!status ? (
          <EmptyState icon={Megaphone} title={t("WINGO N°{n} à générer", { n: number })}>
            {captured
              ? t("Il se génère d'après les résultats de l'OPO N°{n} : bilan, posture du GTIA et bascules des missions de l'OPO N°{next}, qu'il rédige. Vous pourrez le compléter (ennemi, mission, idée de manœuvre, Kill Box, FRAGO LOG), le relire puis le diffuser.", { n: opo, next: opo + 1 })
              : t("Il se génère une fois les résultats de l'OPO N°{n} saisis.", { n: opo })}
          </EmptyState>
        ) : (
          <div className="mx-auto flex max-w-[860px] flex-col gap-10 px-8 py-10 max-sm:px-4">
            {!layoutQuery.data && <EmptyState variant="loading" compact title={t("Mise en page…")} />}
            {wingo &&
              layoutQuery.data?.pages.map((p, i) => (
                <OrderPage
                  key={i === 0 ? `reveal-${wingo.id}-${wingo.version}` : p.number}
                  className={i === 0 ? "opo-reveal" : undefined}
                  page={p}
                  layout={layoutQuery.data}
                  imageUrl={imageUrl}
                  activeField={null}
                  editable={false}
                  onPick={() => undefined}
                />
              ))}
          </div>
        )}
      </div>
      <Presence show={dialog === "complete"}>
        {dialog === "complete" && (
          <WingoDialog
            exerciseId={exerciseId}
            number={opo}
            generated={status !== null}
            onClose={() => setDialog(null)}
            onApplied={(result) => {
              refreshAfterWingo(queryClient, exerciseId, result);
              setDialog(null);
              if (result.notice) notify({ text: t("WINGO N°{n} généré", { n: number }), detail: tServer(result.notice), tone: "warn" });
              else notify({ text: t("WINGO N°{n} généré : l'OPO N°{next} est rédigé", { n: number, next: opo + 1 }), tone: "ok" });
            }}
          />
        )}
      </Presence>
      <Presence show={dialog === "signature"}>
        {dialog === "signature" && (
          <SignaturePad
            hasSignature={Boolean(wingo?.has_signature)}
            onClose={() => setDialog(null)}
            onSave={(png) => sign.mutate(png)}
            onDelete={() => unsign.mutate()}
          />
        )}
      </Presence>
    </div>
  );
}
